/*
 * Copyright (c) 2019 Frontier Developments PLC.
 */


import {JsonLdItem} from '@/store/types';
import {RewardAchievementSet, TrophyGrade} from '@/store/modules/trophy-cabinet/types';

/**
 * Active Community Challenge State Interface
 */
export interface ActiveCommunityChallengeStateInterface {
    challenge: CommunityChallengeInterface;
    count: number;
    milestones: CommunityChallengeMilestoneInterface[];
    achievements: CommunityChallengeAchievementInterface[];
    claimable: CommunityChallengeAchievementInterface[];
}

/**
 * Active Community Challenge State class.
 */
export class ActiveCommunityChallengeState implements ActiveCommunityChallengeStateInterface {
    public challenge!: CommunityChallengeInterface;
    public count: number = 0;
    public milestones: CommunityChallengeMilestoneInterface[] = [];
    public achievements: CommunityChallengeAchievementInterface[] = [];
    public claimable: CommunityChallengeAchievementInterface[] = [];
}

/**
 * State response interface.
 */
export interface ActiveCommunityChallengeStateResponseInterface
    extends JsonLdItem, ActiveCommunityChallengeStateInterface {
}


/**
 * Community Challenge Interface
 */
export interface CommunityChallengeInterface extends JsonLdItem {
    id: string;
    category: string;
    event: string;
    target: string;
    species: string;
    title: string;
    synopsis: string;
    description: string;
    quantity: number;
    total: number;
    image: string;
    completed: boolean;
    commenced: boolean;
    expired: boolean;
    percentile: PercentileInterface;
    serverTimestamp: number;
    commencesAtTimestamp: number;
    expiresAtTimestamp: number;
    milestone?: CommunityChallengeMilestoneInterface[];
    contribution?: number;
    trophy?: TrophyGrade;
    rewards?: RewardAchievementSet;
    percentileContribution?: number;
}

/**
 * Represents a set of percentiles
 */
export interface PercentileInterface {
    [key: string]: number;
}

/**
 * Milestones to be completed during a Community Challenge
 */
export interface CommunityChallengeMilestoneInterface extends JsonLdItem {
    type: string;
    quantity: number;
    description: string;
    rewards: CommunityChallengeRewardInterface[];
    id: number;
}

/**
 * Rewards for Milestones
 */
export interface CommunityChallengeRewardInterface extends JsonLdItem {
    id: string;
    amount?: number;
    accessory?: string;
}

/**
 * Community Challenge Achievements
 */
export interface CommunityChallengeAchievementInterface extends JsonLdItem {
    milestoneId: string;
    claimedAtTimestamp: number|null;
    claimable: boolean;
}

/**
 * Community Challenge Achievements Claim interface
 */
export interface AchievementClaimResponseInterface extends JsonLdItem {
    achievement: CommunityChallengeAchievementInterface;
    animals: string[];
    inventory: string[];
    cc: number;
}

/**
 * Representation of a game achievement notification
 */
export interface GameAchievementAnnounceInterface {
    [key: string]: number|string;
}


export enum MilestoneType {
    COMPLETION = 'completion',
    PERSONAL = 'personal',
}

